import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Clock3, RotateCcw } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Checkbox } from '../../shared/ui/Checkbox';
import { StatusBanner } from '../../shared/ui/StatusBanner';

import { listMyTodo, setMyTaskDone, TODO_KEYS, type TodoItem } from './todo-api';
import s from './TodoCard.module.css';

/** Named before the card stops listing and starts counting. */
const PREVIEW_LIMIT = 5;

function dueLabel(item: TodoItem, now: dayjs.Dayjs): string | null {
  if (item.due_at === null) return null;
  const due = dayjs(item.due_at);
  if (due.isBefore(now, 'day')) return `Overdue ${due.format('D MMM')}`;
  if (due.isSame(now, 'day')) return 'Due today';
  if (due.isSame(now.add(1, 'day'), 'day')) return 'Due tomorrow';
  return `Due ${due.format('D MMM')}`;
}

/**
 * What the checkbox on this row will actually DO, in the student's words.
 *
 * This is the fix for phase 179b's real defect: every delegated line offered
 * the same unlabelled checkbox, whether ticking it closed the institute's
 * record or merely reported it and waited. One tick was final, the other
 * started a conversation, and nothing on screen said which.
 */
function tickLabel(item: TodoItem): string {
  return item.policy === 'student_signals'
    ? `Tell your institute you have done "${item.title}"`
    : `Mark "${item.title}" as done`;
}

/**
 * "What your institute needs from you" on the student home.
 *
 * Replaces the documents-only card (179b): a student does not think in terms
 * of which table an obligation lives in, and three separate cards for tests,
 * files and everything-else is how a to-do gets missed. The list is a
 * read-time union (MESSAGING_PLAN.md D3), so nothing here can disagree with
 * the page it links to.
 *
 * Phase 180 gave every row a `policy` and a `state`, which is what turned the
 * claim/confirm loop from invisible into legible — see `tickLabel` above and
 * the state lines below.
 *
 * Renders nothing when there is nothing outstanding — the rule it shares with
 * `ContinueCard` and `UnreadMessagesCard`.
 */
export function TodoCard(): JSX.Element | null {
  const queryClient = useQueryClient();
  const now = dayjs();

  const query = useQuery({
    queryKey: TODO_KEYS.list,
    queryFn: listMyTodo,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  });

  const tick = useMutation({
    mutationFn: ({ id, done }: { id: string; done: boolean }) => setMyTaskDone(id, done),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: TODO_KEYS.list });
    },
  });

  const items = query.data ?? [];
  if (items.length === 0) return null;

  const outstanding = items.filter((item) => !item.done).length;
  const shown = items.slice(0, PREVIEW_LIMIT);

  return (
    <section className={s.root} aria-labelledby="todo-card-title">
      <header className={s.head}>
        <h2 className={s.title} id="todo-card-title">
          {outstanding === 0
            ? 'Nothing outstanding'
            : outstanding === 1
              ? '1 thing to do'
              : `${String(outstanding)} things to do`}
        </h2>
        <p className={s.hint}>What your institute is waiting on.</p>
      </header>

      {tick.isError ? (
        <StatusBanner tone="error">{(tick.error as Error).message}</StatusBanner>
      ) : null}

      <ul className={s.list}>
        {shown.map((item) => {
          const due = dueLabel(item, now);
          return (
            <li key={`${item.kind}-${item.id}`} className={s.row}>
              {item.can_tick ? (
                <Checkbox
                  checked={item.done}
                  aria-label={tickLabel(item)}
                  disabled={tick.isPending}
                  onCheckedChange={(next) => {
                    tick.mutate({ id: item.id, done: next === true });
                  }}
                />
              ) : (
                <span className={s.bullet} aria-hidden="true" />
              )}

              <Link className={item.done ? s.bodyDone : s.body} to={item.href}>
                <span className={s.rowTitle}>{item.title}</span>
                <span className={s.rowSub}>{item.subtitle}</span>

                {/* The other half of the fix. After the tick the row has to say
                    what happened to it, or "reported and waiting" and "closed"
                    are the same struck-through line. */}
                {item.state === 'checking' ? (
                  <span className={s.waiting}>
                    <Clock3 aria-hidden="true" className={s.stateIcon} />
                    {item.kind === 'document'
                      ? 'Sent — your institute is checking it'
                      : 'Reported — waiting for your institute to confirm'}
                  </span>
                ) : null}
                {item.state === 'sent_back' ? (
                  <span className={s.sentBack}>
                    <RotateCcw aria-hidden="true" className={s.stateIcon} />
                    Your institute has asked for this again
                  </span>
                ) : null}
                {item.state === 'todo' && item.policy === 'student_signals' ? (
                  <span className={s.willCheck}>
                    Your institute will check this after you mark it done
                  </span>
                ) : null}
              </Link>

              {due !== null && !item.done ? (
                <span className={due.startsWith('Overdue') ? s.late : s.due}>{due}</span>
              ) : null}
            </li>
          );
        })}
      </ul>

      {items.length > shown.length ? (
        <p className={s.more}>
          and {items.length - shown.length} more — see Assignments, Documents and Messages.
        </p>
      ) : null}
    </section>
  );
}
